/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { isApiSuccess } from '../../../constants/platformStatusCodes';
import {
  generateMoreSegments,
  getAudienceSegments,
  getAudienceSummary,
  saveSegmentSelection,
} from './reportAudiencesApi';
import { normalizeAudienceSegment } from './utils/audienceModel';
import normalizeAudienceSummary from './utils/normalizeAudienceSummary';

/**
 * Audience module of the Intelligence Report (store key `ReportAudiences`).
 *
 * A report's Audience layer is an EXISTING Audience (`audienceReport.id` from GET /intel-reports —
 * the backend creates it from POST /intel-reports), followed through the audience endpoints
 * (reportAudiencesApi.js — the routes the Audiences module uses, without depending on its slice):
 *   GET /audiences/{id}/segments  — `loadReportAudience` → status + segments (REST is the truth)
 *   private channel `audience.{id}` — progress / segment / completed / failed events
 *                                     (ReportAudienceListener)
 *   segments/selection             — `setReportSegmentVote` → Like / Dislike a segment
 *   GET /audiences/{id}/summary    — `loadAudienceSummary` → the summary card
 *   segments/generate-more         — `regenerateFromFeedback` → "Regenerate from feedback"
 * A segment's vote is its `status` (`selectionStatus`): 'Accept' = liked, 'Reject' = disliked,
 * anything else = no vote. Clearing a vote saves it as `neutralSegmentIds`; disliked segments stay
 * listed (the backend returns them as 'Reject'), and only segments a Replace regenerate swaps out
 * are archived. "New" = the segment came from the latest Generate-more batch. `audiences[id]` holds
 * what the report view shows.
 */
const initialState = {
  // audienceId → { status (Audience's own, e.g. 'Processing'), segments, influencersPending,
  //                progress (0–100 | null), loading, loaded, error,
  //                pendingVotes: { segmentId → vote } while a vote is being saved (shown only as
  //                              the button's spinner; `vote` changes once the save succeeds),
  //                regeneration: null | { status: 'generating' | 'done', mode, referenceIds,
  //                                        dislikedIds, beforeIds, hiddenIds, percent, newIds } }
  audiences: {},
};

const emptyAudience = {
  status: null,
  segments: [],
  influencersPending: false,
  progress: null,
  loading: false,
  loaded: false,
  error: '',
  pendingVotes: {},
  regeneration: null,
  summary: null, // normalizeAudienceSummary output, or null until / unless it loads
};

/** "Regenerate from liked" modes (Figma 2322-23216). */
export const REGENERATE_MODE = {
  REPLACE: 'replace', // hide the other segments (sent as disliked) and add similar ones
  KEEP: 'keep', // keep everything and add similar ones
};

/**
 * A segment's Like / Dislike: one of the two, or `null` for no vote.
 * @typedef {'like'|'dislike'|null} SegmentVote
 */

/** The two votes a segment can hold (`null` = none). */
export const VOTE = { LIKE: 'like', DISLIKE: 'dislike' };

const LIKED = 'Accept';
const DISLIKED = 'Reject';

/**
 * @param {string|null|undefined} status - the segment's `status` from GET /segments
 * @returns {SegmentVote}
 */
const voteFromStatus = (status) => {
  if (status === LIKED) return VOTE.LIKE;
  if (status === DISLIKED) return VOTE.DISLIKE;
  return null;
};

const entry = (state, id) => {
  const key = String(id);
  if (!state.audiences[key]) state.audiences[key] = { ...emptyAudience };
  return state.audiences[key];
};

const ReportAudiencesSlice = createSlice({
  name: 'ReportAudiences',
  initialState,
  reducers: {
    reportAudienceLoading(state, action) {
      entry(state, action.payload).loading = true;
    },
    reportAudienceLoaded(state, action) {
      const { id, status, segments, influencersPending } = action.payload;
      const current = entry(state, id);
      const { regeneration } = current;
      // Segments hidden by a regeneration stay out even if a GET raced the hide.
      const listed = regeneration
        ? segments.filter((segment) => !regeneration.hiddenIds.includes(segment.id))
        : segments;
      Object.assign(current, {
        status: status ?? current.status,
        segments: listed,
        influencersPending,
        loading: false,
        loaded: true,
        error: '',
      });
      // A regeneration is done once segments it didn't start with show up (socket or poll re-sync).
      if (regeneration?.status === 'generating') {
        const newIds = listed
          .map((segment) => segment.id)
          .filter((segmentId) => !regeneration.beforeIds.includes(segmentId));
        if (newIds.length) Object.assign(regeneration, { status: 'done', newIds, percent: null });
      }
    },
    reportAudienceLoadFailed(state, action) {
      const { id, error } = action.payload;
      Object.assign(entry(state, id), { loading: false, error });
    },
    audienceProgressChanged(state, action) {
      const { id, percent } = action.payload;
      const value = Number(percent);
      entry(state, id).progress = Number.isFinite(value) ? value : null;
    },
    audienceStatusChanged(state, action) {
      const { id, status } = action.payload;
      Object.assign(entry(state, id), { status, progress: null });
    },
    audienceSummaryLoaded(state, action) {
      const { id, summary } = action.payload;
      entry(state, id).summary = summary;
    },
    regenerationStarted(state, action) {
      const { id, mode, referenceIds, dislikedIds, hiddenIds } = action.payload;
      const current = entry(state, id);
      // Replaced segments disappear right away (the backend archives them); the user's own dislikes
      // stay listed.
      current.segments = current.segments.filter((segment) => !hiddenIds.includes(segment.id));
      current.regeneration = {
        status: 'generating',
        mode,
        referenceIds,
        dislikedIds,
        beforeIds: current.segments.map((segment) => segment.id),
        hiddenIds,
        percent: null,
        newIds: [],
      };
    },
    regenerationProgress(state, action) {
      const { id, percent } = action.payload;
      const { regeneration } = entry(state, id);
      const value = Number(percent);
      if (regeneration?.status === 'generating' && Number.isFinite(value)) {
        regeneration.percent = value;
      }
    },
    regenerationCleared(state, action) {
      entry(state, action.payload).regeneration = null;
    },
    segmentVoteSaving(state, action) {
      const { id, segmentId, vote } = action.payload;
      entry(state, id).pendingVotes[segmentId] = vote;
    },
    segmentVoteSaved(state, action) {
      const { id, segmentId, vote } = action.payload;
      const current = entry(state, id);
      delete current.pendingVotes[segmentId];
      current.segments = current.segments.map((segment) =>
        segment.id === segmentId ? { ...segment, vote } : segment
      );
    },
    segmentVoteFailed(state, action) {
      const { id, segmentId } = action.payload;
      delete entry(state, id).pendingVotes[segmentId];
    },
  },
});

export const {
  audienceProgressChanged,
  audienceStatusChanged,
  regenerationCleared,
  regenerationProgress,
} = ReportAudiencesSlice.actions;
const {
  reportAudienceLoading,
  reportAudienceLoaded,
  reportAudienceLoadFailed,
  segmentVoteSaving,
  segmentVoteSaved,
  segmentVoteFailed,
  regenerationStarted,
  audienceSummaryLoaded,
} = ReportAudiencesSlice.actions;

/** The report view's state for one existing audience (an empty entry until it's loaded). */
export const selectReportAudience = (state, audienceId) =>
  (audienceId != null && state.ReportAudiences.audiences[String(audienceId)]) || emptyAudience;

/**
 * Segment as the report shows it: the normalised segment (audienceModel), plus the card's
 * separate "summary" (outline) and "why this fits" texts (the normaliser folds them together),
 * its vote, and the generation batch it came from (for "New").
 * @typedef {Object} ReportSegment
 * @property {string} id - the `segmentId`
 * @property {string} name
 * @property {SegmentVote} vote
 * @property {string|null} batchId - `respData.user_input.segment_request_id`
 * @property {string|null} createdAt
 * @property {string} summary
 * @property {string|null} whyThisFits
 * @property {boolean} isArchived
 * @property {string|null} enrichmentStatus
 * @property {boolean} [isNew] - set by `markNewSegments`
 */

/**
 * @param {Object} raw - one segment of GET /audiences/{id}/segments
 * @returns {ReportSegment}
 */
const toReportSegment = (raw) => ({
  ...normalizeAudienceSegment(raw),
  vote: voteFromStatus(raw.status),
  batchId: raw.respData?.user_input?.segment_request_id ?? null,
  createdAt: raw.respData?.createdAt ?? null,
  summary:
    raw.respData?.outline?.description ??
    raw.respData?.summary?.textGenerate ??
    raw.description ??
    '',
  whyThisFits: raw.whyThisFits ?? null,
});

/**
 * "New" = the segment belongs to the latest generation batch (`respData.user_input
 * .segment_request_id`), and only once the audience has more than one batch — i.e. it came from a
 * Generate-more run. The latest batch is the one holding the most recently created segment.
 */
const markNewSegments = (segments) => {
  const batches = new Set(segments.map((segment) => segment.batchId).filter((b) => b != null));
  if (batches.size < 2) return segments.map((segment) => ({ ...segment, isNew: false }));
  const newest = segments.reduce((latest, segment) =>
    String(segment.createdAt || '') > String(latest.createdAt || '') ? segment : latest
  );
  return segments.map((segment) => ({
    ...segment,
    isNew: segment.batchId != null && segment.batchId === newest.batchId,
  }));
};

/**
 * GET /audiences/{id}/segments for an existing audience. Keeps the active segments (not archived /
 * cancelled — the same count rule as the Audiences module).
 *
 * @param {string|number} audienceId
 * @param {{ silent?: boolean }} options - silent refreshes don't flag loading
 * @returns {Promise<{ ok: boolean, status?: string }>}
 */
export const loadReportAudience = (audienceId, { silent = false } = {}) => async (dispatch) => {
  if (audienceId == null) return { ok: false };
  const id = String(audienceId);
  if (!silent) dispatch(reportAudienceLoading(id));
  try {
    const resp = await getAudienceSegments(id);
    if (!isApiSuccess(resp) || !resp?.data) {
      dispatch(
        reportAudienceLoadFailed({
          id,
          error: resp?.message || resp?.error || "We couldn't load this audience.",
        })
      );
      return { ok: false };
    }
    const { status, segments = [], influencersPending } = resp.data;
    const active = markNewSegments(
      segments
        .map(toReportSegment)
        .filter((segment) => !segment.isArchived && segment.enrichmentStatus !== 'cancelled')
    );
    dispatch(
      reportAudienceLoaded({
        id,
        status,
        segments: active,
        influencersPending: influencersPending === true,
      })
    );
    return { ok: true, status };
  } catch (error) {
    dispatch(reportAudienceLoadFailed({ id, error: "We couldn't load this audience." }));
    return { ok: false };
  }
};

/**
 * GET /audiences/{id}/summary — the Audience layer's summary card (reach, matched influencers,
 * core demographic, top locations, shared interests). A failed or empty response keeps the last
 * summary (the card simply doesn't show until there is one).
 *
 * @returns {Promise<{ ok: boolean }>}
 */
export const loadAudienceSummary = (audienceId) => async (dispatch) => {
  if (audienceId == null) return { ok: false };
  const id = String(audienceId);
  const resp = await getAudienceSummary(id);
  const summary = isApiSuccess(resp) ? normalizeAudienceSummary(resp?.data) : null;
  if (summary) dispatch(audienceSummaryLoaded({ id, summary }));
  return { ok: Boolean(summary) };
};

// The API client aborts an in-flight call when another goes to the same endpoint, and every vote
// goes to segments/selection — so votes save one after the other, per audience.
const selectionSaves = new Map();
const queueSelectionSave = (audienceId, save) => {
  const previous = selectionSaves.get(audienceId) || Promise.resolve();
  const next = previous.then(save, save);
  selectionSaves.set(audienceId, next);
  next
    .finally(() => {
      if (selectionSaves.get(audienceId) === next) selectionSaves.delete(audienceId);
    })
    .catch(() => {}); // the caller handles the failure; this chain only cleans up
  return next;
};

const isSaved = (resp) => isApiSuccess(resp) || (resp?.httpStatus >= 200 && resp?.httpStatus < 300);

/** Ids of the audience's segments holding `vote`. */
const idsWithVote = (segments, vote) =>
  segments.filter((segment) => segment.vote === vote).map((segment) => segment.id);

/**
 * Like / Dislike a segment through PATCH segments/selection (ids are segmentId values), or clear
 * its vote (`null`, sent as `neutralSegmentIds`). Clearing saves like any other vote.
 * The vote applies once the save succeeds (until then it's only `pendingVotes`, shown as the button's
 * spinner), so a failed save changes nothing but the error.
 *
 * @param {string|number} audienceId
 * @param {string} segmentId
 * @param {SegmentVote} vote
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const setReportSegmentVote = (audienceId, segmentId, vote) => async (dispatch) => {
  const id = String(audienceId);
  dispatch(segmentVoteSaving({ id, segmentId, vote }));
  let resp;
  try {
    resp = await queueSelectionSave(id, () =>
      saveSegmentSelection(
        id,
        vote === VOTE.LIKE ? [segmentId] : [],
        vote === VOTE.DISLIKE ? [segmentId] : [],
        vote === null ? [segmentId] : []
      )
    );
  } catch (error) {
    resp = null;
  }
  const ok = isSaved(resp);
  dispatch(ok ? segmentVoteSaved({ id, segmentId, vote }) : segmentVoteFailed({ id, segmentId }));
  // Our copy, not the client's (a blocked or dropped request reads "Request cancelled").
  return ok ? { ok: true } : { ok: false, error: "We couldn't save your feedback." };
};

/**
 * "Clear feedback" (feedback bar): every vote goes back to none, saved in one segments/selection
 * call as `neutralSegmentIds`. Like a single vote, it applies once the save succeeds.
 *
 * @param {string|number} audienceId
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const clearReportSegmentFeedback = (audienceId) => async (dispatch, getState) => {
  const id = String(audienceId);
  const votedIds = selectReportAudience(getState(), id)
    .segments.filter((segment) => segment.vote !== null)
    .map((segment) => segment.id);
  if (!votedIds.length) return { ok: true };

  votedIds.forEach((segmentId) => dispatch(segmentVoteSaving({ id, segmentId, vote: null })));
  let resp;
  try {
    resp = await queueSelectionSave(id, () => saveSegmentSelection(id, [], [], votedIds));
  } catch (error) {
    resp = null;
  }
  const ok = isSaved(resp);
  votedIds.forEach((segmentId) =>
    dispatch(
      ok ? segmentVoteSaved({ id, segmentId, vote: null }) : segmentVoteFailed({ id, segmentId })
    )
  );
  return ok ? { ok: true } : { ok: false, error: "We couldn't clear your feedback." };
};

/**
 * "Regenerate from feedback" (confirm modal, Figma 2322-23216) through POST
 * /audiences/{id}/segments/generate-more. The liked segments go as `likedSegmentIds` (the
 * reference) and the disliked ones as `dislikedSegmentIds` (avoided; they stay listed). REPLACE
 * also sends every segment without a vote as `replacedSegmentIds` — the backend doesn't infer "the
 * rest", and archives them, so they leave the list as soon as the call succeeds. KEEP leaves them
 * alone. `feedback` is optional (≤ 5000 chars). How many segments come back is up to
 * the model: the generating state (2323-24148) ends when new segments appear in a re-sync
 * (reportAudienceLoaded).
 *
 * @param {string|number} audienceId
 * @param {string} mode - a REGENERATE_MODE value
 * @param {string} [feedback]
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const regenerateFromFeedback = (audienceId, mode, feedback = '') => async (
  dispatch,
  getState
) => {
  const id = String(audienceId);
  const { segments } = selectReportAudience(getState(), id);
  const referenceIds = idsWithVote(segments, VOTE.LIKE);
  if (!referenceIds.length) return { ok: false, error: 'Like at least one segment first.' };
  const dislikedIds = idsWithVote(segments, VOTE.DISLIKE);
  const hiddenIds = mode === REGENERATE_MODE.REPLACE ? idsWithVote(segments, null) : [];
  const note = String(feedback || '').trim();

  let resp;
  try {
    resp = await generateMoreSegments(id, {
      feedback: note,
      likedSegmentIds: referenceIds,
      dislikedSegmentIds: dislikedIds,
      replacedSegmentIds: hiddenIds,
    });
  } catch (error) {
    resp = null;
  }
  if (!isSaved(resp)) {
    return {
      ok: false,
      error: resp?.message || resp?.error || "We couldn't regenerate the segments.",
    };
  }
  dispatch(regenerationStarted({ id, mode, referenceIds, dislikedIds, hiddenIds }));
  return { ok: true };
};

export default ReportAudiencesSlice.reducer;
